<svelte:options customElement="roi-header" />

<script lang="ts">
    import { Attributes } from '@bayer-int/portletce-shared';
    import type { TitleAlign } from 'portlet/models/settings/settingDefinitions/titleAlignSetting';
    import Header from './Header.svelte';

    import type { InteractiveTitleSettingDefinition } from './types';

    interface Props {
        title: string;
        subtitle: string;
        titleAlign: TitleAlign;
        replacementTags?: Record<string, string>;
        interactiveReplacementTags?: Record<string, InteractiveTitleSettingDefinition>;
        nomenu?: string;
        maximized?: string;
        jumptoaoenabled?: string;
        showsupportform?: string;
        externalinfourlenabled?: string;
        isDataToDownload?: string;
    }

    let {
        title,
        subtitle,
        titleAlign,
        replacementTags = {},
        interactiveReplacementTags = {},
        nomenu = 'false',
        maximized = 'false',
        jumptoaoenabled = 'false',
        showsupportform = 'true',
        externalinfourlenabled = 'false',
        isDataToDownload = 'false',
    }: Props = $props();

    let component: HTMLElement | undefined = $state();

    let parsedAttrs = $derived({
        title: title || '',
        subtitle: subtitle || '',
        titleAlign: titleAlign || 'center',
        replacementTags: replacementTags || {},
        interactiveReplacementTags: interactiveReplacementTags || {},
        nomenu: Attributes.parseBool(nomenu),
        maximized: Attributes.parseBool(maximized),
        jumpToAOEnabled: Attributes.parseBool(jumptoaoenabled),
        showSupportForm: Attributes.parseBool(showsupportform),
        externalInfoUrlEnabled: Attributes.parseBool(externalinfourlenabled),
        isDataToDownload: Attributes.parseBool(isDataToDownload),
    });

    function forwardEvent(event: CustomEvent) {
        component?.dispatchEvent(
            new CustomEvent(event.type, {
                detail: event.detail,
                bubbles: true,
                cancelable: true,
                composed: true,
            }),
        );
    }
</script>

<svelte:head>
    <style>
        @font-face {
            font-family: 'Material Icons';
            font-style: normal;
            font-weight: 400;
            font-display: block;
            src: url(https://fonts.gstatic.com/s/materialicons/v128/flUhRq6tzZclQEJ-Vdg-IuiaDsNcIhQ8tQ.woff2)
                format('woff2');
        }
    </style>
</svelte:head>

<div bind:this={component}>
    <Header
        title={parsedAttrs.title}
        subtitle={parsedAttrs.subtitle}
        titleAlign={parsedAttrs.titleAlign}
        replacementTags={parsedAttrs.replacementTags}
        interactiveReplacementTags={parsedAttrs.interactiveReplacementTags}
        nomenu={parsedAttrs.nomenu}
        maximized={parsedAttrs.maximized}
        isDataToDownload={parsedAttrs.isDataToDownload}
        externalInfoUrlEnabled={parsedAttrs.externalInfoUrlEnabled}
        jumpToAOEnabled={parsedAttrs.jumpToAOEnabled}
        showSupportForm={parsedAttrs.showSupportForm}
        on:portlet-maximize={forwardEvent}
        on:jump-to-ao={forwardEvent}
        on:portlet-menu-item-click={forwardEvent}
        on:interactive-title-change={forwardEvent}
        on:open-info-url={forwardEvent}
        on:download-data={forwardEvent}
    >
        <slot name="header-prefix" slot="header-prefix" />
        <slot name="header-postfix" slot="header-postfix" />
    </Header>
</div>

<style lang="scss">
    @import '../common/styles/global.scss';

    :host {
        --title-align: var(--title-align, center);
        --title-font-size: var(--title-font-size, 1em);
        --subtitle-font-size: var(--subtitle-font-size, 0.85em);
        --title-font-weight: var(--title-font-weight, normal);
        --title-color: var(--title-color, black);
        --subtitle-color: var(--subtitle-color, gray);
        --title-margin: var(--title-margin, 1em 0);
    }
</style>
